/**
 * Consistent undo for the canonical dispatch milestone model.
 *
 * Undoing a milestone must also clear every LATER completed milestone —
 * otherwise the tracker ends up with holes (e.g. "Delivered" done while
 * "Picked Up" is empty). This walks the derived stage list backwards from the
 * end down to the target, undoes each completed actionable stage using the
 * existing per-stop / per-dispatch undo writers, clears the legacy status_*
 * fallback events that would otherwise resurrect the stage after a refetch,
 * then re-syncs the derived `direct_dispatches.status` column.
 *
 * Uploaded documents (BOL/POD/etc.) and load notes are NEVER touched.
 */
import { supabase } from "@/integrations/supabase/client";
import type { DerivedStage } from "@/lib/lrDirect/deriveStages";
import { undoStopStage, undoDispatchStageKey } from "@/hooks/useUpdateDispatchStatus";
import { isActionableStage, legacyStatusForStages } from "@/lib/lrDirect/milestoneActions";

export async function undoMilestoneCascade(args: {
  stages: DerivedStage[];
  target: DerivedStage;
  dispatchId: string;
  loadId: string;
  actorId: string;
  /** Whether the broker/carrier has accepted the dispatch (legacy status floor). */
  accepted: boolean;
}): Promise<void> {
  const { stages, target, dispatchId, loadId, actorId, accepted } = args;
  const targetIdx = stages.findIndex((s) => s.key === target.key);
  if (targetIdx < 0) return;

  // Later → earlier, so each undo sees a consistent state.
  const toUndo = stages
    .slice(targetIdx)
    .filter((s) => isActionableStage(s) && s.status === "done")
    .reverse();

  for (const s of toUndo) {
    if (s.kind === "broker_dispatch") {
      await undoDispatchStageKey({
        dispatchId,
        stageKey: s.key as "truck_en_route" | "in_transit",
        actorId,
      });
    } else if (s.stopId && s.stopType && s.stopNumber != null && s.subKind) {
      await undoStopStage({
        dispatchId,
        loadId,
        stopId: s.stopId,
        stopNumber: s.stopNumber,
        stopType: s.stopType,
        subKind: s.subKind,
        actorId,
      });
    }
  }

  // Clear the LEGACY status_* fallbacks that deriveStages() still consults —
  // otherwise an undone stage reappears on the next refetch. `status_closed`
  // is always cleared because Closed sits to the right of every milestone.
  const legacyTypes = new Set<string>(["status_closed"]);
  const rolledBack = stages.slice(targetIdx);
  if (rolledBack.some((s) => s.stopType === "delivery" || s.key === "delivered")) {
    legacyTypes.add("status_delivered");
    legacyTypes.add("status_at_delivery");
  }
  if (rolledBack.some((s) => s.stopType === "pickup")) {
    legacyTypes.add("status_picked_up");
    legacyTypes.add("status_at_pickup");
  }
  if (rolledBack.some((s) => s.key === "in_transit")) {
    legacyTypes.add("status_en_route_to_delivery");
  }
  const { error: legacyErr } = await supabase
    .from("direct_dispatch_events" as any)
    .delete()
    .eq("dispatch_id", dispatchId)
    .in("event_type", Array.from(legacyTypes))
    .select("id");
  if (legacyErr) throw legacyErr;

  // Recompute the derived legacy status column from what's left.
  const undoneKeys = new Set(toUndo.map((s) => s.key));
  const remaining: DerivedStage[] = stages.map((s) =>
    undoneKeys.has(s.key) || s.key === "closed"
      ? { ...s, status: "future", at: null }
      : s,
  );
  const nextStatus = legacyStatusForStages(remaining, { accepted, closed: false });

  const { data: updRows, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update({ status: nextStatus, updated_at: new Date().toISOString() })
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updRows || updRows.length === 0) {
    throw new Error("Undo failed: dispatch status was not updated. You may not have permission.");
  }
}
